import { Field, mergeClasses } from "@fluentui/react-components";
import {
	ChangeEvent,
	KeyboardEvent,
	useCallback,
	useEffect,
	useId,
	useRef,
	useState,
} from "react";
import { useIntl } from "react-intl";
import { ContactLabel } from "@/components/atoms/contactLabel";
import { useDebounce } from "@/common/helpers/useDebounce";
import { captureError } from "@/common/helpers/sentryHelper";
import { ContactFilterValue } from "@/repository/models";
import { fetchContactFilterValues } from "@/repository/queryFns/contacts";
import { MaxSuggestions, MaxValuesPerField } from "./advancedFilterUtils";
import { useStyle } from "./style";
import { AdvancedFilterFieldProps } from "./types";

const SuggestionDebounceMs = 300;
const NoActiveSuggestion = -1;

/**
 * The values the server returned, kept together with the text they answer. A list can only be described as
 * empty once its own question has come back, so the field never reports that a value does not exist while
 * the answer is still on its way, or was never asked for at all, which is the case while the field is full.
 */
interface SuggestionAnswer {
	query: string;
	values: ContactFilterValue[];
}

const NoAnswer: SuggestionAnswer = { query: "", values: [] };

/** The nearest ancestor that scrolls, which is what clips an absolutely positioned list. */
const findScrollParent = (element: HTMLElement): HTMLElement | null => {
	let parent = element.parentElement;

	while (parent) {
		const { overflowY } = window.getComputedStyle(parent);

		if (overflowY === "auto" || overflowY === "scroll") {
			return parent;
		}

		parent = parent.parentElement;
	}

	return null;
};

/**
 * Picks values for one advanced-filter field. The suggestion list is an absolutely positioned
 * element inside this field rather than a floating layer, so it overlays the fields below it and
 * stays inside the surrounding dialog.
 */
export const AdvancedFilterField = (
	props: AdvancedFilterFieldProps
): JSX.Element => {
	const { field, label, selected, onChange, onEnter } = props;
	const intl = useIntl();
	const classes = useStyle();
	const listId = useId();

	const [query, setQuery] = useState<string>("");
	const [answer, setAnswer] = useState<SuggestionAnswer>(NoAnswer);
	const [hasFocus, setHasFocus] = useState<boolean>(false);
	const [isDismissed, setIsDismissed] = useState<boolean>(false);
	const [activeIndex, setActiveIndex] = useState<number>(NoActiveSuggestion);
	const [opensUpward, setOpensUpward] = useState<boolean>(false);
	const requestRef = useRef<AbortController | undefined>();
	const fieldRef = useRef<HTMLDivElement>(null);

	const isFull = selected.length >= MaxValuesPerField;
	const selectedValues = selected.map((entry: ContactFilterValue) => entry.value);

	const available = answer.values
		.filter(
			(suggestion: ContactFilterValue) =>
				!selectedValues.includes(suggestion.value)
		)
		.slice(0, MaxSuggestions);

	const loadSuggestions = useCallback(
		(text: string) => {
			requestRef.current?.abort();

			const asked = text.trim();

			if (!asked) {
				setAnswer(NoAnswer);

				return;
			}

			const request = new AbortController();
			requestRef.current = request;

			fetchContactFilterValues(field, text, request.signal)
				.then((values: ContactFilterValue[]) => {
					if (!request.signal.aborted) {
						setAnswer({ query: asked, values });
						setActiveIndex(NoActiveSuggestion);
					}
				})
				.catch((error: Error) => {
					if (!request.signal.aborted) {
						// The question stays unanswered, so the list says nothing rather than "no such value".
						captureError(error, "Loading contact filter values failed");
					}
				});
		},
		[field]
	);

	const [debouncedLoadSuggestions, cancelPendingLoad] = useDebounce(
		loadSuggestions,
		SuggestionDebounceMs
	);

	// Nothing should still be on its way in once the field is gone.
	useEffect(() => {
		return () => {
			cancelPendingLoad();
			requestRef.current?.abort();
		};
	}, [cancelPendingLoad]);

	const clearQuery = useCallback((): void => {
		// A keystroke queued just before the pick would otherwise land after it.
		cancelPendingLoad();
		requestRef.current?.abort();
		setQuery("");
		setAnswer(NoAnswer);
		setActiveIndex(NoActiveSuggestion);
	}, [cancelPendingLoad]);

	const onQueryChange = (event: ChangeEvent<HTMLInputElement>): void => {
		setQuery(event.target.value);
		setIsDismissed(false);

		// A full field asks nothing; removing a value below asks whatever was typed meanwhile.
		if (!isFull) {
			debouncedLoadSuggestions(event.target.value);
		}
	};

	/**
	 * Takes a value. Holding the modifier keeps the search and its list up, so an agent who wants three
	 * cities takes all three from the one list instead of searching three times.
	 */
	/* The list covers the fields under it, so closing on a plain pick is what keeps the ordinary case of
	   one value from costing a dismiss before the next field can be reached. */
	const pick = (suggestion: ContactFilterValue, keepListOpen: boolean): void => {
		if (!isFull) {
			onChange([...selected, suggestion]);
		}

		if (keepListOpen) {
			// The text goes either way; what the modifier keeps is the list. The values on screen are still
			// the ones the agent asked for, so the next pick comes from the same list, and nothing is left
			// in the box to be cleared by hand once they are done.
			cancelPendingLoad();
			setQuery("");
			setActiveIndex(NoActiveSuggestion);

			return;
		}

		clearQuery();
	};

	const remove = (value: string): void => {
		onChange(selected.filter((entry: ContactFilterValue) => entry.value !== value));

		if (isFull) {
			// Anything typed while the field was full went unasked; the freed slot asks it now.
			loadSuggestions(query);
		}
	};

	const isOpen = hasFocus && !isDismissed;
	const hasOptions = isOpen && !isFull && available.length > 0;

	// What the list has to say, if anything: the values left to take, the cap, or that the search now on
	// screen came back with nothing.
	const isShowingSuggestions =
		isOpen &&
		(isFull ||
			available.length > 0 ||
			(!!query.trim() && answer.query === query.trim()));

	// Holding the modifier gains nothing with one slot left before the cap, so the hint stops there.
	const canTakeSeveral =
		available.length > 1 && selected.length < MaxValuesPerField - 1;

	const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
		// An IME sends Enter and the arrow keys to its own candidate list while a word is being composed,
		// so acting on them here would take a value or apply the filter in the middle of a word.
		if (event.nativeEvent.isComposing) {
			return;
		}

		if (event.key === "Escape") {
			// Escape closes the list it is shown over, and only reaches the dialog once there is no list
			// left to close. The dialog would otherwise go with the list, discarding every selection made
			// since it opened.
			if (isShowingSuggestions) {
				event.preventDefault();
				event.stopPropagation();
			}

			setIsDismissed(true);
			setActiveIndex(NoActiveSuggestion);

			return;
		}

		const last = selected[selected.length - 1];

		if (event.key === "Backspace" && !query && last) {
			remove(last.value);

			return;
		}

		// Enter takes the highlighted value, and with the modifier held it leaves the list up for the next
		// one. With nothing highlighted it is the dialog's, which applies the filter.
		if (event.key === "Enter") {
			// Nothing highlighted, or a list that shrank under the highlight: nothing left to take here.
			const active = available[activeIndex];

			if (active) {
				event.preventDefault();
				pick(active, event.ctrlKey || event.metaKey);

				return;
			}

			onEnter?.();

			return;
		}

		if (!hasOptions) {
			return;
		}

		if (event.key === "ArrowDown") {
			event.preventDefault();
			setActiveIndex((index: number) => (index + 1) % available.length);

			return;
		}

		if (event.key === "ArrowUp") {
			event.preventDefault();
			setActiveIndex((index: number) =>
				index <= 0 ? available.length - 1 : index - 1
			);

			return;
		}
	};

	// The last field sits against the bottom of a dialog that scrolls, so the list opens towards
	// whichever side has more room rather than always downwards.
	useEffect(() => {
		if (!isOpen || !fieldRef.current) {
			return;
		}

		const rect = fieldRef.current.getBoundingClientRect();
		const scrollParent = findScrollParent(fieldRef.current);
		const bounds = scrollParent?.getBoundingClientRect();
		const spaceBelow = (bounds?.bottom ?? window.innerHeight) - rect.bottom;
		const spaceAbove = rect.top - (bounds?.top ?? 0);

		setOpensUpward(spaceBelow < spaceAbove);
	}, [isOpen, available.length, isFull]);

	const suggestionsClass = opensUpward
		? mergeClasses(classes.suggestions, classes.suggestionsAbove)
		: classes.suggestions;

	const optionId = (index: number): string => `${listId}-option-${index}`;

	const renderSuggestions = (): JSX.Element | null => {
		if (!isShowingSuggestions) {
			return null;
		}

		if (isFull) {
			return (
				<div className={suggestionsClass}>
					<div className={classes.suggestionMessage}>
						{intl.formatMessage(
							{ id: "Contacts.AdvancedFilter.MaximumReached" },
							{ count: MaxValuesPerField }
						)}
					</div>
				</div>
			);
		}

		if (available.length > 0) {
			return (
				// NOSONAR typescript:S6819 -- a native select or datalist cannot offer a filtered,
				// multi-value list with removable chips and the arrow-key model this field needs.
				<div className={suggestionsClass}>
					<div
						id={listId}
						className={classes.suggestionList}
						role="listbox"
						aria-label={label}
					>
						{available.map((suggestion: ContactFilterValue, index: number) => (
							<div
								key={suggestion.value}
								id={optionId(index)}
								role="option"
								aria-selected={index === activeIndex}
								className={
									index === activeIndex
										? mergeClasses(classes.suggestion, classes.suggestionActive)
										: classes.suggestion
								}
								// Chosen on mouse down: a click would blur the input and close the list first.
								onMouseDown={(event) => {
									event.preventDefault();
									pick(suggestion, event.ctrlKey || event.metaKey);
								}}
							>
								{suggestion.label}
							</div>
						))}
					</div>
					{canTakeSeveral && (
						<div className={classes.suggestionHint}>
							{intl.formatMessage({
								id: "Contacts.AdvancedFilter.MultiSelectHint",
							})}
						</div>
					)}
				</div>
			);
		}

		return (
			<div className={suggestionsClass}>
				<div className={classes.suggestionMessage}>
					{intl.formatMessage({ id: "Contacts.AdvancedFilter.NoSuggestions" })}
				</div>
			</div>
		);
	};

	return (
		<Field label={label}>
			<div className={classes.field} ref={fieldRef}>
				<div className={classes.control}>
					{selected.map((entry: ContactFilterValue) => (
						<ContactLabel
							key={entry.value}
							text={entry.label}
							appearance={undefined}
							buttonAriaLabel={intl.formatMessage(
								{ id: "Contacts.AdvancedFilter.RemoveValue" },
								{ value: entry.label }
							)}
							onButtonClick={() => remove(entry.value)}
						/>
					))}
					<input
						type="text"
						role="combobox"
						aria-label={label}
						aria-expanded={hasOptions}
						aria-autocomplete="list"
						aria-controls={hasOptions ? listId : undefined}
						aria-activedescendant={
							activeIndex === NoActiveSuggestion ? undefined : optionId(activeIndex)
						}
						className={classes.input}
						value={query}
						placeholder={intl.formatMessage({
							id: "Contacts.AdvancedFilter.Placeholder",
						})}
						onChange={onQueryChange}
						onKeyDown={onKeyDown}
						onFocus={() => {
							setHasFocus(true);
							// Dismissing is for the visit that pressed Escape; coming back asks for the list again.
							setIsDismissed(false);
						}}
						onBlur={() => setHasFocus(false)}
					/>
				</div>
				{renderSuggestions()}
			</div>
		</Field>
	);
};
